import { useState } from 'react';
import { m } from 'framer-motion';
import { ArrowRight, Server, Shield, Code } from 'lucide-react';
import { ContactModal } from './ContactModal';

export const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden">
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} subject="Аренда сервера (Популярный тариф)" />
      
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[#0f172a]">
        <div className="absolute inset-0 bg-[url('/images/hero.jpg')] bg-cover bg-center opacity-10 mix-blend-overlay" />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent/20 rounded-full blur-[120px] mix-blend-screen"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-primary/30 rounded-full blur-[100px] mix-blend-screen"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Text Content */}
          <m.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent font-medium text-sm mb-6 border border-accent/20">
              <Code className="w-4 h-4" />
              <span>Комплексный ИТ-аутсорсинг</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              Инфраструктура и разработка для <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-blue-400">масштабирования</span> бизнеса
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              От аренды мощных серверов (VPS/VDS) и настройки сетей до разработки сложных API-решений и интеграции CRM. Локальная поддержка в Красноярске, глобальная надежность.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a 
                href="#pricing"
                className="w-full sm:w-auto px-8 py-4 bg-accent hover:bg-accent-hover text-white rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 glow-effect"
              >
                <span>Подобрать решение</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a 
                href="#features"
                className="w-full sm:w-auto px-8 py-4 bg-white/5 hover:bg-white/10 text-white rounded-xl font-bold transition-all duration-300 border border-white/10"
              >
                Все услуги
              </a>
            </div>
            
            <div className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm font-medium text-slate-400">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Защита от DDoS</span>
              </div>
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-emerald-400" />
                <span>NVMe диски</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                <span>Поддержка 24/7</span>
              </div>
            </div>
          </m.div>

          {/* Visual Content / Server Card */}
          <m.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative lg:ml-auto w-full max-w-md mx-auto hidden md:block"
          >
            <div className="glass-panel rounded-2xl p-6 relative overflow-hidden border border-white/10">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent to-blue-500"></div>
              
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Популярный тариф</h3>
                  <p className="text-sm text-slate-400">Оптимально для бизнеса</p>
                </div>
                <div className="bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-lg text-xs font-bold border border-emerald-500/20">
                  ТОП
                </div>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-slate-400">Процессор</span>
                  <span className="font-semibold text-white">4 Ядра 3.2ГГц</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-slate-400">ОЗУ</span>
                  <span className="font-semibold text-white">8 GB</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-slate-400">Диск</span>
                  <span className="font-semibold text-white">120 GB NVMe</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-slate-400">Локация</span>
                  <span className="font-semibold text-white">Красноярск / Москва</span>
                </div>
              </div>
              
              <div className="flex items-end gap-2 mb-6">
                <span className="text-4xl font-extrabold text-white">799₽</span>
                <span className="text-slate-400 mb-1">/ мес</span>
              </div>
              
              <button 
                onClick={() => setIsModalOpen(true)}
                className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-bold transition-all border border-white/10"
              >
                Заказать сейчас
              </button>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-accent/20 rounded-full blur-2xl"></div>
          </m.div>
        </div>
      </div>
    </section>
  );
};